import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { IconsEnum } from '../../../../shared-modules/shared-icons/models/icons.enum';
import { IconColourEnum } from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import { ModalControllerService } from '../../../shared-services/overlay/modal-controller.service';
import { DynavsoftConfigService } from '../../../../features/barcode-scanner/services/dynamsoft-config.service';
import { CameraPreloadService } from '../../../../features/barcode-scanner/services/camera-preload.service';
import { environment } from '../../../../../environments/environment';
import {
  CameraEnhancer,
  CameraView,
  CaptureVisionRouter,
  DecodedBarcodesResult,
  VideoDeviceInfo,
} from 'dynamsoft-barcode-reader-bundle';
import {isIosDevice, isMobileDevice} from '../../../utils/mobile/mobile.utils';
import { DynamsoftLicenceNotificationService } from '../../../../features/barcode-scanner/services/dynamsoft-licence-notification.service';
import { DEFAULT_LOADER_INFINITE } from '../../../shared-module-ui/models/animations';
import {
  isSolidGreenSample,
  pickAlternateRearCamera,
  pickPreferredRearCamera,
} from '../../../../features/barcode-scanner/utils/camera-preview.utils';

/**
 * Shared reusable barcode scanner modal.
 * Opens the device camera, scans a barcode and returns it to the caller.
 *
 * Modal mode  — opened via ModalControllerService: dismisses with { barcode: string } on confirm.
 * Embedded mode — [persistentMode]="true": emits (barcodeScanned) and hides itself on confirm.
 *
 * The caller is responsible for deciding what to do with the returned barcode.
 */
@Component({
  selector: 'app-shared-barcode-scanner-modal',
  templateUrl: './shared-barcode-scanner-modal.component.html',
  styleUrls: ['./shared-barcode-scanner-modal.component.scss'],
  standalone: false,
})
export class SharedBarcodeScannerModalComponent implements AfterViewInit, OnDestroy {
  @ViewChild('cameraView', { static: true }) cameraViewRef: ElementRef<HTMLDivElement>;

  private cameraEnhancer: CameraEnhancer | null = null;
  private cameraView: CameraView | null = null;
  private captureVisionRouter: CaptureVisionRouter | null = null;

  hasPermission: boolean = false;
  isInitialized: boolean = false;
  private isScanning: boolean = false;
  private hasFoundBarcode: boolean = false;
  private lastBarcodeTime: number = 0;
  private readonly debounceDelay = 1000;
  private pendingBarcodeResult: { text: string; timestamp: number } | null = null;

  isVisible: boolean = false;
  private isPersistentModal: boolean = false;
  isFieldCaptureActive: boolean = false;

  noBarcodeDetected: boolean = false;
  isOpeningCamera: boolean = false;
  cameraPreviewFailed: boolean = false;
  private noBarcodeTimer: ReturnType<typeof setTimeout> | null = null;
  private iosVideoObserver: MutationObserver | null = null;
  #fieldCaptureResolve: ((barcode: string | null) => void) | null = null;
  #openGeneration = 0;
  private readonly previewSampleSize = 8;
  private readonly previewWaitMs = 1500;

  protected readonly icons = IconsEnum;
  protected readonly iconColours = IconColourEnum;
  protected readonly scannerLoaderAnimation = DEFAULT_LOADER_INFINITE;

  /** Emitted in embedded/persistent mode when a barcode is confirmed. */
  @Output() readonly barcodeScanned = new EventEmitter<string>();

  @Input() isTorchAvailable: boolean = false;
  @Input() isTorchOn: boolean = false;
  @Input() usePreloadedCamera: boolean = false;

  constructor(
    private readonly modalControllerService: ModalControllerService,
    private readonly dynavsoftConfigService: DynavsoftConfigService,
    private readonly cameraPreloadService: CameraPreloadService,
    private readonly dynamsoftLicenceNotificationService: DynamsoftLicenceNotificationService,
  ) {}

  @Input() set persistentMode(value: boolean) {
    this.isPersistentModal = value;
  }

  async ngAfterViewInit(): Promise<void> {
    if (this.isPersistentModal) {
      return;
    }
    await this.beginCameraSession();
  }

  ngOnDestroy(): void {
    this.#openGeneration += 1;
    this.iosVideoObserver?.disconnect();
    this.iosVideoObserver = null;
    if (isMobileDevice() && this.isTorchOn) {
      this.onToggleTorch();
    }
    if (this.noBarcodeTimer !== null) {
      clearTimeout(this.noBarcodeTimer);
    }
    this.destroyDynamsoft();
  }

  private async initializeDynamsoft(): Promise<void> {
    this.watchIosCameraPreview();
    try {
      if (this.usePreloadedCamera && this.cameraPreloadService.isReady) {
        await this.usePreloadedCameraInstances();
      } else {
        await this.initializeFreshCamera();
      }
    } catch (error) {
      this.hasPermission = false;
      await this.notifyLicenceFailure(error);
    }
  }

  private async usePreloadedCameraInstances(): Promise<void> {
    try {
      await this.cameraPreloadService.transferCameraToModal(this.cameraViewRef.nativeElement);

      const { cameraEnhancer, cameraView, captureVisionRouter } =
        this.cameraPreloadService.getPreloadedInstances();
      this.cameraEnhancer = cameraEnhancer;
      this.cameraView = cameraView;
      this.captureVisionRouter = captureVisionRouter;

      await this.cameraPreloadService.startCapturing((result: DecodedBarcodesResult) => {
        this.handleBarcodeResult(result);
      });

      this.isInitialized = true;
      this.hasPermission = true;
      this.isScanning = true;
    } catch (error) {
      await this.initializeFreshCamera();
    }
  }

  private async initializeFreshCamera(): Promise<void> {
    const licenseKey = environment.dynamsoft.apiKey;
    await this.dynavsoftConfigService.initializeDynamsoft(licenseKey);

    this.cameraView = await CameraView.createInstance(this.cameraViewRef.nativeElement);
    this.cameraEnhancer = await CameraEnhancer.createInstance(this.cameraView);

    this.captureVisionRouter = await CaptureVisionRouter.createInstance();
    this.captureVisionRouter.setInput(this.cameraEnhancer);

    this.captureVisionRouter.addResultReceiver({
      onDecodedBarcodesReceived: (result: DecodedBarcodesResult) => {
        this.handleBarcodeResult(result);
      },
    });

    await this.openPreferredCamera();
    await this.captureVisionRouter.startCapturing('ReadBarcodes_Balance');

    this.isInitialized = true;
    this.hasPermission = true;
    this.isScanning = true;
  }

  private handleBarcodeResult(result: DecodedBarcodesResult): void {
    const currentTime = Date.now();

    if (this.isPersistentModal && !this.isVisible) {
      return;
    }

    if (!this.isScanning) {
      return;
    }

    if (currentTime - this.lastBarcodeTime < this.debounceDelay) {
      return;
    }

    if (result.barcodeResultItems && result.barcodeResultItems.length > 0) {
      this.pendingBarcodeResult = {
        text: result.barcodeResultItems[0].text,
        timestamp: currentTime,
      };
      this.lastBarcodeTime = currentTime;
    }
  }

  /**
   * Confirms the currently pending barcode.
   * Shows an inline message if no barcode has been detected yet.
   */
  onConfirmScan(): void {
    if (!this.pendingBarcodeResult) {
      this.noBarcodeDetected = true;
      if (this.noBarcodeTimer !== null) {
        clearTimeout(this.noBarcodeTimer);
      }
      this.noBarcodeTimer = setTimeout(() => {
        this.noBarcodeDetected = false;
        this.noBarcodeTimer = null;
      }, 2500);
      return;
    }

    this.hasFoundBarcode = true;
    this.stopScanning();
    const barcodeText = this.pendingBarcodeResult.text;
    this.pendingBarcodeResult = null;
    this.resolveBarcode(barcodeText);
  }

  /**
   * Delivers the confirmed barcode to the caller.
   * In embedded mode: emits barcodeScanned and hides the component.
   * In modal mode: dismisses the Ionic modal with { barcode }.
   */
  private resolveBarcode(barcodeText: string): void {
    if (this.isFieldCaptureActive) {
      this.completeFieldCapture(barcodeText);
      void this.setVisible(false);
      return;
    }
    if (this.isPersistentModal) {
      this.barcodeScanned.emit(barcodeText);
      void this.setVisible(false);
    } else {
      void this.modalControllerService.dismiss({ barcode: barcodeText });
    }
  }

  /**
   * Opens the persistent scanner overlay and resolves with the confirmed barcode (or null if cancelled).
   */
  async openForFieldCapture(): Promise<string | null> {
    if (!this.isPersistentModal || this.#fieldCaptureResolve) {
      return null;
    }
    return new Promise<string | null>((resolve: (barcode: string | null) => void) => {
      this.#fieldCaptureResolve = resolve;
      this.isFieldCaptureActive = true;
      void this.setVisible(true);
    });
  }

  private completeFieldCapture(barcode: string | null): void {
    this.isFieldCaptureActive = false;
    const resolve = this.#fieldCaptureResolve;
    this.#fieldCaptureResolve = null;
    if (resolve) {
      resolve(barcode);
    }
  }

  /**
   * Shows the Dynamsoft licence modal when the thrown error is licence-related, then closes the scanner overlay.
   *
   * @param {unknown} error - Value thrown while initialising or restarting Dynamsoft.
   */
  private async notifyLicenceFailure(error: unknown): Promise<void> {
    const didNotify: boolean =
      await this.dynamsoftLicenceNotificationService.notifyIfLicenceError(error);
    if (didNotify) {
      this.onCloseModal();
    }
  }

  onCloseModal(): void {
    this.#openGeneration += 1;
    this.isVisible = false;
    this.isOpeningCamera = false;
    this.cameraPreviewFailed = false;
    if (isMobileDevice() && this.isTorchOn) {
      this.onToggleTorch();
    }
    if (this.#fieldCaptureResolve) {
      this.completeFieldCapture(null);
    }
    if (this.isPersistentModal) {
      void this.setVisible(false);
    } else {
      void this.modalControllerService.dismiss();
    }
  }

  /**
   * Shows or hides the scanner in embedded/persistent mode.
   * Restarts the camera stream and resets detection state on show.
   */
  async setVisible(visible: boolean): Promise<void> {
    if (!visible) {
      this.#openGeneration += 1;
      this.isVisible = false;
      this.isOpeningCamera = false;
      this.cameraPreviewFailed = false;
      this.isFieldCaptureActive = false;
      if (isMobileDevice() && this.isTorchOn) {
        this.onToggleTorch();
      }
      this.pauseIosCameraPreview();
      this.stopScanning();
      this.closeCameraQuietly();
      return;
    }
    await this.beginCameraSession();
  }

  /**
   * Opens the camera again after the preview stayed blank or green.
   */
  async onRetryCameraPreview(): Promise<void> {
    await this.beginCameraSession();
  }

  /**
   * Shows the scanner immediately, then opens the camera once the preview element has a size.
   * Opening while the view is hidden leaves some phones on a green or blank frame with no error.
   */
  private async beginCameraSession(): Promise<void> {
    const generation = ++this.#openGeneration;
    this.cameraPreviewFailed = false;
    this.isOpeningCamera = true;
    this.isVisible = true;
    try {
      await this.waitForCameraLayout();
      if (!this.isSessionCurrent(generation)) {
        return;
      }
      if (!this.isInitialized) {
        await this.initializeDynamsoft();
      } else {
        await this.dynavsoftConfigService.claimScannerUse();
      }
      if (!this.isSessionCurrent(generation)) {
        this.stopScanning();
        this.closeCameraQuietly();
        return;
      }
      if (!this.isInitialized || !this.hasPermission) {
        if (this.isVisible && this.isSessionCurrent(generation)) {
          this.cameraPreviewFailed = true;
        }
        return;
      }
      if (this.cameraEnhancer && !this.cameraEnhancer.isOpen()) {
        this.cameraEnhancer.ifSkipCameraInspection = true;
        await this.cameraEnhancer.open();
      }
      this.resetBarcodeDetection();
      await this.restartScanning();
      const live = await this.ensureLiveCameraPreview(generation);
      if (!this.isSessionCurrent(generation)) {
        this.stopScanning();
        this.closeCameraQuietly();
        return;
      }
      if (!live) {
        this.cameraPreviewFailed = true;
        return;
      }
      this.resumeIosCameraPreview();
      if (isMobileDevice() && this.isTorchAvailable && !this.isTorchOn) {
        this.onToggleTorch();
      }
    } catch (error) {
      if (!this.isSessionCurrent(generation)) {
        return;
      }
      this.isVisible = false;
      await this.notifyLicenceFailure(error);
    } finally {
      if (this.isSessionCurrent(generation)) {
        this.isOpeningCamera = false;
      }
    }
  }

  /**
   * Waits until the camera element is on screen so the video can attach to a real layout box.
   */
  private async waitForCameraLayout(): Promise<void> {
    for (let attempt = 0; attempt < 10; attempt += 1) {
      const element = this.cameraViewRef?.nativeElement;
      if (element && element.offsetWidth > 0 && element.offsetHeight > 0) {
        return;
      }
      await this.waitForNextPaint();
    }
  }

  /**
   * Lets Angular paint the visible scanner before the camera is opened.
   */
  private waitForNextPaint(): Promise<void> {
    return new Promise((resolve: () => void) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          resolve();
        });
      });
    });
  }

  private isSessionCurrent(generation: number): boolean {
    return generation === this.#openGeneration;
  }

  /**
   * Opens the rear camera and skips Dynamsoft's automatic camera inspection, which can select a feed that stays green.
   */
  private async openPreferredCamera(): Promise<void> {
    if (!this.cameraEnhancer) {
      return;
    }
    this.cameraEnhancer.ifSkipCameraInspection = true;
    const cameras = await this.listCameras();
    const preferred = pickPreferredRearCamera(cameras);
    if (preferred) {
      await this.cameraEnhancer.selectCamera(preferred.deviceId);
    }
    if (!this.cameraEnhancer.isOpen()) {
      await this.cameraEnhancer.open();
    }
  }

  /**
   * Confirms the video element is producing frames. Reopens, then tries another rear camera, before reporting failure.
   *
   * @param {number} generation - Session id used to abandon the check when the user closes the scanner.
   * @returns {Promise<boolean>} True when a non-green frame is showing.
   */
  private async ensureLiveCameraPreview(generation: number): Promise<boolean> {
    if (await this.waitForUsablePreview()) {
      return true;
    }
    if (!this.isSessionCurrent(generation)) {
      return false;
    }
    await this.reopenCameraAtResolution(1280, 720);
    if (await this.waitForUsablePreview()) {
      return true;
    }
    if (!this.isSessionCurrent(generation)) {
      return false;
    }
    const switched = await this.switchToAlternateRearCamera();
    if (!switched) {
      return false;
    }
    return this.waitForUsablePreview();
  }

  /**
   * Polls the video element until it shows a real frame or the wait expires.
   *
   * @returns {Promise<boolean>} True when two consecutive usable frames were seen.
   */
  private async waitForUsablePreview(): Promise<boolean> {
    const started = Date.now();
    let usableStreak = 0;
    while (Date.now() - started < this.previewWaitMs) {
      this.tryPlayVideoElement();
      if (this.isCameraPreviewUsable()) {
        usableStreak += 1;
        if (usableStreak >= 2) {
          return true;
        }
      } else {
        usableStreak = 0;
      }
      await this.delay(200);
    }
    return this.isCameraPreviewUsable();
  }

  /**
   * Closes and reopens the camera at a resolution most phones can actually stream.
   *
   * @param {number} width - Requested frame width.
   * @param {number} height - Requested frame height.
   */
  private async reopenCameraAtResolution(width: number, height: number): Promise<void> {
    if (!this.cameraEnhancer) {
      return;
    }
    this.stopScanning();
    this.closeCameraQuietly();
    this.cameraEnhancer.ifSkipCameraInspection = true;
    await this.cameraEnhancer.setResolution({ width, height });
    if (!this.cameraEnhancer.isOpen()) {
      await this.cameraEnhancer.open();
    }
    await this.restartScanning();
  }

  /**
   * Moves to another rear camera when the current one stays green or blank.
   *
   * @returns {Promise<boolean>} True when a different camera was selected.
   */
  private async switchToAlternateRearCamera(): Promise<boolean> {
    if (!this.cameraEnhancer) {
      return false;
    }
    const cameras = await this.listCameras();
    const nextCamera = pickAlternateRearCamera(cameras, this.readSelectedCameraId());
    if (!nextCamera) {
      return false;
    }
    this.stopScanning();
    this.closeCameraQuietly();
    await this.cameraEnhancer.selectCamera(nextCamera.deviceId);
    if (!this.cameraEnhancer.isOpen()) {
      await this.cameraEnhancer.open();
    }
    await this.restartScanning();
    return true;
  }

  /**
   * @returns {Promise<VideoDeviceInfo[]>} Cameras the browser will expose, or an empty list when listing fails.
   */
  private async listCameras(): Promise<VideoDeviceInfo[]> {
    if (!this.cameraEnhancer) {
      return [];
    }
    try {
      return await this.cameraEnhancer.getAllCameras();
    } catch (error) {
      console.warn('Error listing cameras:', error);
      return [];
    }
  }

  /**
   * @returns {string | undefined} Device id of the camera that is currently selected.
   */
  private readSelectedCameraId(): string | undefined {
    if (!this.cameraEnhancer) {
      return undefined;
    }
    try {
      return this.cameraEnhancer.getSelectedCamera()?.deviceId;
    } catch (error) {
      console.warn('Error reading selected camera:', error);
      return undefined;
    }
  }

  private closeCameraQuietly(): void {
    try {
      this.cameraEnhancer?.close();
    } catch (error) {
      console.warn('Error closing camera before retry:', error);
    }
  }

  private tryPlayVideoElement(): void {
    const video = this.getCameraVideoElement();
    if (!video) {
      return;
    }
    video.muted = true;
    video.setAttribute('playsinline', 'true');
    if (video.paused) {
      video.play().catch(() => undefined);
    }
  }

  /**
   * @returns {boolean} True when the video element has a sized frame that is not a solid green field.
   */
  private isCameraPreviewUsable(): boolean {
    const video = this.getCameraVideoElement();
    if (!video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      return false;
    }
    if (video.videoWidth < 2 || video.videoHeight < 2) {
      return false;
    }
    return !this.isVideoFrameSolidGreen(video);
  }

  /**
   * @param {HTMLVideoElement} video - Live camera element.
   * @returns {boolean} True when the current frame is the flat green placeholder.
   */
  private isVideoFrameSolidGreen(video: HTMLVideoElement): boolean {
    const canvas = document.createElement('canvas');
    canvas.width = this.previewSampleSize;
    canvas.height = this.previewSampleSize;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) {
      return false;
    }
    try {
      context.drawImage(video, 0, 0, this.previewSampleSize, this.previewSampleSize);
    } catch (error) {
      console.warn('Error sampling camera frame:', error);
      return false;
    }
    const pixels = context.getImageData(0, 0, this.previewSampleSize, this.previewSampleSize).data;
    return isSolidGreenSample(pixels);
  }

  /**
   * @returns {HTMLVideoElement | null} The video element Dynamsoft is drawing into.
   */
  private getCameraVideoElement(): HTMLVideoElement | null {
    if (this.cameraEnhancer) {
      try {
        const video = this.cameraEnhancer.getVideoEl();
        if (video) {
          return video;
        }
      } catch (error) {
        console.warn('Error reading camera video element:', error);
      }
    }
    return this.cameraViewRef?.nativeElement?.querySelector('video') ?? null;
  }

  /**
   * @param {number} durationMs - How long to wait.
   */
  private delay(durationMs: number): Promise<void> {
    return new Promise((resolve: () => void) => {
      setTimeout(resolve, durationMs);
    });
  }

  /**
   * Pauses a hidden iOS camera preview so it cannot sit above the stock search sheet.
   */
  pauseHiddenPreview(): void {
    this.pauseIosCameraPreview();
  }

  /**
   * Marks the live preview as inline and pauses it so a hidden camera cannot cover later modals on iOS.
   */
  private pauseIosCameraPreview(): void {
    this.updateIosCameraPreview(true);
  }

  /**
   * Resumes the inline camera preview after the scanner overlay is shown again.
   */
  private resumeIosCameraPreview(): void {
    this.updateIosCameraPreview(false);
  }

  /**
   * Sets playsinline as soon as Dynamsoft inserts the preview, before iOS can promote it to the native player.
   */
  private watchIosCameraPreview(): void {
    if (!isIosDevice() || this.iosVideoObserver) {
      return;
    }
    const host = this.cameraViewRef?.nativeElement;
    if (!host) {
      return;
    }
    this.markIosCameraVideosInline(host);
    this.iosVideoObserver = new MutationObserver((): void => {
      this.markIosCameraVideosInline(host);
    });
    this.iosVideoObserver.observe(host, {childList: true, subtree: true});
  }

  /**
   * Keeps the Dynamsoft video in the page instead of the native iOS player, and pauses it while hidden.
   * @param pause When true, playback is paused
   */
  private updateIosCameraPreview(pause: boolean): void {
    if (!isIosDevice()) {
      return;
    }
    const host = this.cameraViewRef?.nativeElement;
    if (!host) {
      return;
    }
    host.querySelectorAll('video').forEach((video: HTMLVideoElement): void => {
      this.markIosCameraVideosInline(video);
      if (pause) {
        video.pause();
        return;
      }
      video.play().catch((): void => undefined);
    });
  }

  /**
   * Marks a video, or every video under a container, to play inside the page on iOS.
   * @param target Video element or container to update
   */
  private markIosCameraVideosInline(target: ParentNode | HTMLVideoElement): void {
    const videos = target instanceof HTMLVideoElement
      ? [target]
      : Array.from(target.querySelectorAll('video'));
    videos.forEach((video: HTMLVideoElement): void => {
      video.setAttribute('playsinline', 'true');
      video.setAttribute('webkit-playsinline', 'true');
    });
  }

  private stopScanning(): void {
    if (this.isScanning) {
      try {
        if (this.usePreloadedCamera && this.cameraPreloadService.isReady) {
          this.cameraPreloadService.stopCapturing();
        } else if (this.captureVisionRouter) {
          this.captureVisionRouter.stopCapturing();
        }
        this.isScanning = false;
      } catch (error) {}
    }
  }

  private async restartScanning(): Promise<void> {
    if (this.isScanning) {
      return;
    }
    try {
      if (this.usePreloadedCamera && this.cameraPreloadService.isReady) {
        await this.cameraPreloadService.startCapturing((result: DecodedBarcodesResult) => {
          this.handleBarcodeResult(result);
        });
      } else if (this.captureVisionRouter) {
        await this.captureVisionRouter.startCapturing('ReadBarcodes_Balance');
      }
      this.isScanning = true;
    } catch (error) {
      console.error('Error restarting scanning:', error);
      await this.notifyLicenceFailure(error);
    }
  }

  private resetBarcodeDetection(): void {
    this.hasFoundBarcode = false;
    this.lastBarcodeTime = 0;
    this.pendingBarcodeResult = null;
    this.noBarcodeDetected = false;
    this.clearBarcodeOverlays();
  }

  private clearBarcodeOverlays(): void {
    try {
      if (this.cameraView) {
        const barcodeLayer = this.cameraView.getDrawingLayer(2);
        if (barcodeLayer) {
          barcodeLayer.clearDrawingItems();
        }
      }
    } catch (error) {
      console.warn('Error clearing barcode overlays:', error);
    }
  }

  private destroyDynamsoft(): void {
    this.stopScanning();

    if (this.captureVisionRouter) {
      try {
        this.captureVisionRouter.dispose().catch((error: unknown) => {
          console.error(error);
        });
      } catch (error) {}
      this.captureVisionRouter = null;
    }

    if (this.cameraEnhancer) {
      try {
        this.cameraEnhancer.close();
        this.cameraEnhancer.dispose();
      } catch (error) {
        console.warn('Error disposing camera:', error);
      }
      this.cameraEnhancer = null;
    }

    if (this.cameraView) {
      try {
        this.cameraView.dispose();
      } catch (error) {
        console.warn('Error disposing camera view:', error);
      }
      this.cameraView = null;
    }
  }

  onToggleTorch(): void {
    this.isTorchOn = !this.isTorchOn;
    try {
      if (this.usePreloadedCamera && this.cameraPreloadService.isReady) {
        this.cameraPreloadService.toggleTorch(this.isTorchOn);
      } else if (this.cameraEnhancer) {
        if (this.isTorchOn) {
          this.cameraEnhancer.turnOnTorch().catch((error: unknown) => {
            console.warn('Error turning on torch:', error);
          });
        } else {
          this.cameraEnhancer.turnOffTorch().catch((error: unknown) => {
            console.warn('Error turning off torch:', error);
          });
        }
      }
    } catch (error) {
      console.warn('Error toggling torch:', error);
    }
  }
}
